/**
 * 知识问答页面（新版）- 现代化设计
 * 多轮对话界面，支持来源引用展示
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  Plus,
  Trash2, Pencil,
  MessageSquare,
  FileText,
  Loader2,
  ChevronLeft,
  BookOpen,
  X
} from 'lucide-react';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/Toast';
import { useConfirm } from '@/components/ConfirmModal';
import styles from './KnowledgeChat.module.css';

interface Message {
  id: number;
  role: 'user' | 'assistant';
  content: string;
  sources?: {
    archive_id: number;
    archive_name: string;
    file_name?: string;
    snippet: string;
  }[];
  create_time: string;
}

interface Session {
  session_id: string;
  title: string | null;
  update_time: string;
  message_count: number;
}

export default function KnowledgeChatNewPage() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const confirmDialog = useConfirm();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [showSidebar, setShowSidebar] = useState(() => {
    // 移动端默认折叠侧边栏
    if (typeof window !== 'undefined') {
      return window.innerWidth > 768;
    }
    return true;
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 加载会话列表
  useEffect(() => {
    loadSessions();
  }, []);

  // 监听窗口大小变化，移动端自动折叠侧边栏
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setShowSidebar(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      setLoading(true);
      const response = await api.getChatSessions();
      if (response.data) {
        setSessions(response.data);
        // 首次加载时自动打开最近会话
        if (!currentSessionId && response.data.length > 0) {
          loadHistory(response.data[0].session_id);
        }
      }
    } catch (err) {
      console.error('加载会话列表失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadHistory = async (sessionId: string) => {
    try {
      setLoading(true);
      const response = await api.getChatHistory(sessionId);
      if (response.data) {
        setCurrentSessionId(sessionId);
        // 转换消息格式
        const convertedMessages: Message[] = response.data.messages.map(msg => ({
          id: msg.message_id,
          role: msg.role,
          content: msg.content,
          sources: msg.sources?.map(s => ({
            archive_id: s.archive_id,
            archive_name: s.archive_name,
            file_name: s.file_name,
            snippet: s.snippet,
          })),
          create_time: msg.create_time,
        }));
        setMessages(convertedMessages);
      }
    } catch (err) {
      console.error('加载对话历史失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const startNewChat = () => {
    setCurrentSessionId(null);
    setMessages([]);
    setInput('');
  };

  // 会话重命名（行内编辑）
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');

  const startRename = (sessionId: string, currentTitle: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingSessionId(sessionId);
    setEditingTitle(currentTitle || '');
  };

  const savingRenameRef = useRef(false);

  const saveRename = async (sessionId: string) => {
    if (savingRenameRef.current) return;  // Enter 与 blur 可能先后触发，防重复提交
    const title = editingTitle.trim();
    if (!title) {
      showToast('warning', '会话名称不能为空');
      return;
    }
    savingRenameRef.current = true;
    try {
      await api.renameChatSession(sessionId, title);
      setSessions(prev => prev.map(s2 => s2.session_id === sessionId ? { ...s2, title } : s2));
      setEditingSessionId(null);
      showToast('success', '会话已重命名');
    } catch (err) {
      showToast('error', '重命名失败');
    } finally {
      savingRenameRef.current = false;
    }
  };

  const cancelRename = () => setEditingSessionId(null);

  const deleteSession = async (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = await confirmDialog.confirm({
      title: '确认删除',
      message: '确定要删除这个对话吗？此操作不可恢复。',
      confirmText: '确认删除',
      danger: true,
    });
    if (!confirmed) return;

    try {
      await api.deleteChatSession(sessionId);
      setSessions((prev) => prev.filter((s) => s.session_id !== sessionId));
      if (currentSessionId === sessionId) {
        startNewChat();
      }
      showToast('success', '删除成功');
    } catch (err) {
      showToast('error', '删除失败');
    }
  };

  // 知识范围：all=全部 / daily=日常档案 / quality=质量事故 / 具体类型
  const [scope, setScope] = useState<string>('daily');
  // 两档知识范围：非质量档案统一归入"日常档案"
  const SCOPE_OPTIONS = [
    { value: 'daily', label: '日常档案' },
    { value: 'quality', label: '质量档案' },
  ];

  // 切换范围：开启新对话（避免多轮上下文跨范围串扰）
  const handleScopeChange = (newScope: string) => {
    if (newScope === scope) return;
    setScope(newScope);
    if (currentSessionId || messages.length > 0) {
      setCurrentSessionId(null);
      setMessages([]);
      showToast('info', '已切换知识范围，开启新对话');
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || sending) return;

    const userMessage = input.trim();
    setInput('');

    // 添加用户消息到列表
    const tempUserMsg: Message = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
      create_time: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempUserMsg]);
    setSending(true);

    try {
      const response = await api.knowledgeChat({
        question: userMessage,
        session_id: currentSessionId || undefined,
        scope,
      });

      if (response.data) {
        // 更新会话ID
        if (!currentSessionId) {
          setCurrentSessionId(response.data.session_id);
          loadSessions();
        }

        // 添加助手消息
        const assistantMsg: Message = {
          id: Date.now() + 1,
          role: 'assistant',
          content: response.data.answer,
          sources: response.data.sources?.map(s => ({
            archive_id: s.archive_id,
            archive_name: s.archive_name,
            file_name: s.file_name,
            snippet: s.snippet,
          })),
          create_time: new Date().toISOString(),
        };
        setMessages((prev) => [...prev, assistantMsg]);
      }
    } catch (err) {
      const errorMsg: Message = {
        id: Date.now() + 1,
        role: 'assistant',
        content: err instanceof Error ? err.message : '回答生成失败，请重试',
        create_time: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className={styles.container}>
      {/* 侧边栏 */}
      <AnimatePresence>
        {showSidebar && (
          <motion.aside
            className={styles.sidebar}
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 320, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className={styles.sidebarHeader}>
              <div className={styles.sidebarTitleRow}>
                <div className={styles.sidebarTitle}>
                  <MessageSquare size={24} />
                  <h2>对话历史</h2>
                </div>
                <button
                  className={styles.sidebarCloseBtn}
                  onClick={() => setShowSidebar(false)}
                  aria-label="关闭侧边栏"
                >
                  <X size={20} />
                </button>
              </div>
              <button className={styles.newChatBtn} onClick={startNewChat}>
                <Plus size={18} />
                <span>新建对话</span>
              </button>
            </div>

            <div className={styles.sessionList}>
              {loading && sessions.length === 0 ? (
                <div className={styles.loadingState}>
                  <Loader2 className={styles.spinner} size={24} />
                  <span>加载中...</span>
                </div>
              ) : sessions.length === 0 ? (
                <div className={styles.emptyState}>
                  <MessageSquare size={32} />
                  <p>暂无对话记录</p>
                  <p className={styles.hint}>开始新对话吧</p>
                </div>
              ) : (
                sessions.map((session) => (
                  <motion.div
                    key={session.session_id}
                    className={`${styles.sessionItem} ${
                      currentSessionId === session.session_id ? styles.active : ''
                    }`}
                    onClick={() => loadHistory(session.session_id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className={styles.sessionAvatar}>
                      <MessageSquare size={18} />
                    </div>
                    <div className={styles.sessionInfo}>
                      {editingSessionId === session.session_id ? (
                        <input
                          autoFocus
                          value={editingTitle}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onKeyDown={(e) => {
                            e.stopPropagation();
                            if (e.key === 'Enter') saveRename(session.session_id);
                            if (e.key === 'Escape') cancelRename();
                          }}
                          onBlur={() => editingSessionId === session.session_id && saveRename(session.session_id)}
                          maxLength={100}
                          className={styles.sessionTitleInput}
                        />
                      ) : (
                        <span className={styles.sessionTitle}>
                          {session.title || '新对话'}
                        </span>
                      )}
                      <div className={styles.sessionMeta}>
                        <span className={styles.sessionTime}>
                          {new Date(session.update_time).toLocaleDateString('zh-CN')}
                        </span>
                        <span className={styles.sessionCount}>
                          {session.message_count} 条消息
                        </span>
                      </div>
                    </div>
                    {editingSessionId !== session.session_id && (
                      <button
                        className={styles.deleteBtn}
                        onClick={(e) => startRename(session.session_id, session.title || '', e)}
                        aria-label="重命名对话"
                        title="重命名"
                      >
                        <Pencil size={14} />
                      </button>
                    )}
                    <button
                      className={styles.deleteBtn}
                      onClick={(e) => deleteSession(session.session_id, e)}
                      aria-label="删除对话"
                      style={editingSessionId === session.session_id ? { display: 'none' } : undefined}
                    >
                      <Trash2 size={16} />
                    </button>
                  </motion.div>
                ))
              )}
            </div>
          </motion.aside>
        )}
      </AnimatePresence>

      {/* 移动端遮罩层：点击关闭侧边栏 */}
      {showSidebar && (
        <div
          className={styles.sidebarOverlay}
          onClick={() => setShowSidebar(false)}
          aria-hidden="true"
        />
      )}

      {/* 主内容区 */}
      <main className={styles.main}>
        {/* 顶部工具栏 */}
        <div className={styles.topToolbar}>
          <button
            className={styles.toggleBtn}
            onClick={() => setShowSidebar(!showSidebar)}
            aria-label={showSidebar ? "隐藏侧边栏" : "显示侧边栏"}
          >
            <ChevronLeft
              size={24}
              style={{
                transform: showSidebar ? 'rotate(0deg)' : 'rotate(180deg)',
                transition: 'transform 0.3s ease'
              }}
            />
          </button>
          <h1 className={styles.pageTitle}>知识问答</h1>
          <div className={styles.toolbarSpacer} />
        </div>

        {/* 消息列表 */}
        <div className={styles.messagesContainer}>
          {messages.length === 0 ? (
            <div className={styles.welcomeContainer}>
              <motion.div
                className={styles.welcomeCard}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className={styles.welcomeIcon}>
                  <MessageSquare size={48} />
                </div>
                <h2 className={styles.welcomeTitle}>欢迎使用知识问答</h2>
                <p className={`${styles.welcomeDescription} ${styles.welcomeScopeHint}`}>
                  可对公司制度规范、产品技术、材料说明及质量事故等知识提问，回答将引用知识库资料
                </p>
                <div className={styles.welcomeFeatures}>
                  <div className={styles.featureItem}>
                    <FileText size={20} />
                    <span>支持制度规范 / 产品技术 / 材料说明 / 质量事故</span>
                  </div>
                  <div className={styles.featureItem}>
                    <BookOpen size={20} />
                    <span>智能引用知识库内容回答</span>
                  </div>
                </div>
              </motion.div>
            </div>
          ) : (
            <div className={styles.messages}>
              <AnimatePresence>
                {messages.map((msg, index) => (
                  <motion.div
                    key={msg.id}
                    className={`${styles.message} ${styles[msg.role]}`}
                    initial={{ opacity: 0, y: 20, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <div className={styles.messageAvatar}>
                      {msg.role === 'user' ? (
                        <div className={styles.userAvatar}>
                          {user?.user_name?.charAt(0) || 'U'}
                        </div>
                      ) : (
                        <div className={styles.assistantAvatar}>
                          <FileText size={20} />
                        </div>
                      )}
                    </div>
                    <div className={styles.messageContent}>
                      <div className={styles.messageText}>{msg.content}</div>
                      {msg.sources && msg.sources.length > 0 && (
                        <div className={styles.sources}>
                          <span style={{fontSize:'.6875rem',color:'#9ca3af',marginRight:4}}>参考：</span>
                          {msg.sources.map((s, i) => (
                            <span key={i} style={{fontSize:'.6875rem',color:'#6366f1',background:'#eef2ff',padding:'1px 8px',borderRadius:10,marginRight:4}}>
                              {s.archive_name}{s.file_name ? ` · ${s.file_name}` : ''}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {sending && (
                <motion.div
                  className={`${styles.message} ${styles.assistant}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className={styles.messageAvatar}>
                    <div className={styles.assistantAvatar}>
                      <FileText size={20} />
                    </div>
                  </div>
                  <div className={styles.messageContent}>
                    <div className={styles.typingIndicator}>
                      <div className={styles.typingDot}></div>
                      <div className={styles.typingDot}></div>
                      <div className={styles.typingDot}></div>
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* 任务显示框 - 在消息区域和输入区域之间 */}
        {/* 已移除主内容区的任务框，只保留在侧边栏 */}

        {/* 输入区域 */}
        <motion.div
          className={styles.inputArea}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className={styles.inputWrapper}>
            <select
              value={scope}
              onChange={(e) => handleScopeChange(e.target.value)}
              title="知识范围：日常档案（制度/产品/材料/共享学习档案）或质量档案"
              className={styles.scopeSelect}
            >
              {SCOPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`就${scope === 'quality' ? '质量档案' : '日常档案'}提问，按 Enter 发送...`}
              rows={1}
              disabled={sending}
              className={styles.textarea}
            />
            <button
              className={`${styles.sendBtn} ${input.trim() && !sending ? styles.sendBtnActive : ''}`}
              onClick={sendMessage}
              disabled={!input.trim() || sending}
              aria-label="发送消息"
            >
              {sending ? (
                <Loader2 className={styles.spinner} size={20} />
              ) : (
                <Send size={20} />
              )}
            </button>
          </div>
        </motion.div>
      </main>
    </div>
  );
}